// src/components/billing/EditBillingItem.jsx
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BillingItemsService from "../../../../services/BillingItemsService";
import { AuthContext } from "../../../../context/AuthContext";

const EditBillingItem = () => {
  const { billingItemId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [billingItem, setBillingItem] = useState({
    title: "",
    description: "",
    amount: "",
    currency: "INR",
    status: "PENDING",
    dueDate: ""
  });
  
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user has admin permissions
    const adminCheck = ["ADMIN", "CEO", "VAIBHAV", "EMP"].includes(user?.role);
    setIsAdmin(adminCheck);

    // If not admin, redirect to dashboard
    if (!adminCheck) {
      navigate("/dashboard");
      return;
    }

    const fetchBillingItem = async () => {
      try {
        setLoading(true);
        const data = await BillingItemsService.getBillingItemById(billingItemId);
        
        // Format date to YYYY-MM-DD for input field
        const formattedDueDate = data.dueDate ? 
          new Date(data.dueDate).toISOString().split('T')[0] : '';
        
        // Set form data
        setBillingItem({
          title: data.title || "",
          description: data.description || "",
          amount: data.amount ? parseFloat(data.amount) : "",
          currency: data.currency || "INR",
          status: data.status || "PENDING",
          dueDate: formattedDueDate
        });
        
        // Save original data for reference
        setOriginalData(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching billing item:", err);
        setError("Failed to load billing item details.");
        setLoading(false);
      }
    };

    fetchBillingItem();
  }, [billingItemId, user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingItem({
      ...billingItem,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      await BillingItemsService.updateBillingItem(billingItemId, billingItem);
      
      // Redirect back to billing item view
      navigate(`/dashboard/billing-items/${billingItemId}`);
    } catch (err) {
      console.error("Error updating billing item:", err);
      setError("Failed to update billing item.");
      setLoading(false);
    }
  };

  if (loading && !originalData) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md my-4" role="alert">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-start mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Edit Billing Item</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-gray-50 p-6 rounded-md mb-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={billingItem.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={billingItem.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              ></textarea>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount
              </label>
              <input
                type="number"
                name="amount"
                value={billingItem.amount}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                name="currency"
                value={billingItem.currency}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                name="dueDate"
                value={billingItem.dueDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={billingItem.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="DRAFT">Draft</option>
                <option value="PENDING">Pending</option>
                <option value="PAID">Paid</option>
                <option value="OVERDUE">Overdue</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Client and Project Info Display */}
        {originalData && (
          <div className="bg-gray-50 p-4 rounded-md mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Related Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {originalData.project && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Project</p>
                  <p className="text-gray-900 mt-1">
                    {typeof originalData.project === 'object' ? originalData.project.name : 'Loading...'}
                  </p>
                </div>
              )}
              {originalData.client && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Client</p>
                  <p className="text-gray-900 mt-1">
                    {typeof originalData.client === 'object' ? originalData.client.companyName : 'Loading...'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Payment Summary */}
        {originalData && originalData.paidAmount && originalData.paidAmount.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-md mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Payment Summary</h2>
            <div className="text-gray-700">
              <p>This billing item has {originalData.paidAmount.length} payment record(s). You cannot modify existing payments here.</p>
              <p className="mt-2">To add a new payment, please use the "Add Payment" option on the billing item details page.</p>
            </div>
          </div>
        )}
        
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/billing-items/${billingItemId}`)}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditBillingItem;